import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import AfspraakDialog from "@/components/AfspraakDialog";
import AfspraakAfsluitDialog from "@/components/AfspraakAfsluitDialog";
import { useMyLocations } from "@/components/MijnLocaties";
import { currentWeek } from "@/lib/week";
import { usePersistentCollapse } from "@/hooks/usePersistentCollapse";
import { Plus, Inbox } from "lucide-react";
import { LoadError, errMessage } from "@/components/LoadError";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import { NONE_KEY, INTERN_KEY, isIntern, GEEN_OPVOLGER, GROEP, EIGENAAR_LABEL, groepVan, compareRows, compareGroepen, groepSleutel, groepLabel, sorteerAfgerondLijst, plural, cap, opvolgerChips, pastOpvolger, startOpvolger, pastEigenaarAfdeling } from "@/components/afspraken/afsprakenUtils";
import { AfsprakenToolbar } from "@/components/afspraken/AfsprakenToolbar";
import { CounterTiles } from "@/components/afspraken/CounterTiles";
import { LocatieGroepen } from "@/components/afspraken/LocatieGroepen";
import { AfspraakBoard } from "@/components/afspraken/AfspraakBoard";
import { AfspraakRow } from "@/components/afspraken/AfspraakRow";
import { AfspraakDetail } from "@/components/afspraken/AfspraakDetail";
import { SnippetContext, norm, heeftBesluit, reactieTreft, reactieSnippet } from "@/components/afspraken/reactieUtils";

function useMediaQuery(q) {
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const h = () => setM(mq.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, [q]);
  return m;
}

const segBtn = (on) => `px-3 py-1.5 rounded-sm text-sm font-semibold transition-colors ${on ? "bg-zinc-950 text-white" : "text-zinc-500 hover:bg-zinc-100"}`;
const AFGEROND_FILTERS = [{ key: "alle", label: "Alle" }, { key: "nagekomen", label: "Nagekomen" }, { key: "niet", label: "Niet nagekomen" }];
const noteKey = (id, cid) => `${id}|${cid}`;
const cidOf = (c) => c.id || c.date;

export default function Afspraken() {
  const { user } = useAuth();
  const prefsKey = `tw1_afspraken_prefs_${user.username || "anon"}`;
  const initialPrefs = (() => {
    try { return JSON.parse(localStorage.getItem(prefsKey)) || {}; } catch { return {}; }
  })();
  const [afspraken, setAfspraken] = useState([]);
  const [verwijderdeLijst, setVerwijderdeLijst] = useState([]);
  const [extraAf, setExtraAf] = useState([]);
  const [verwijderdError, setVerwijderdError] = useState("");
  const [verwijderdGeladen, setVerwijderdGeladen] = useState(false);
  const [weggelaten, setWeggelaten] = useState(0);
  const [historieWeken, setHistorieWeken] = useState(0);
  const [volledigeHistorie, setVolledigeHistorie] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [metaError, setMetaError] = useState("");
  const [tab, setTab] = useState("lopend");
  const [afgerondFilter, setAfgerondFilter] = useState("alle");
  const [zoek, setZoek] = useState("");
  const [teller, setTeller] = useState(null);
  // Bewaard per gebruiker: weergave, sortering, opvolger en de inhoud van Filters (niet zoek/teller).
  const [view, setView] = useState(initialPrefs.view === "bord" ? "bord" : "lijst");
  const [sortBy, setSortBy] = useState(initialPrefs.sortBy ?? "deadline");
  const [opvolgerFilter, setOpvolgerFilter] = useState(() => startOpvolger(initialPrefs, user));   // v18 C1 / R1 R2
  const [opvolgerGekozen, setOpvolgerGekozen] = useState(initialPrefs.opvolgerGekozen === true);
  const kiesOpvolger = (v) => { setOpvolgerFilter(v); setOpvolgerGekozen(true); };
  const [blContact, setBlContact] = useState({});
  const [afdelingen, setAfdelingen] = useState([]);
  const [eigenaarFilter, setEigenaarFilter] = useState(initialPrefs.eigenaarFilter ?? "alle");
  const [afdelingFilter, setAfdelingFilter] = useState(initialPrefs.afdelingFilter ?? "alle");   // v19-N3; oude prefs zonder sleutel = "alle"
  // N3: de twee spreken elkaar nooit tegen: een afdeling kiezen zet eigenaar op "afdeling"; een andere eigenaar wist de afdeling.
  const kiesAfdeling = (v) => { setAfdelingFilter(v); if (v !== "alle") setEigenaarFilter("afdeling"); };
  const kiesEigenaar = (v) => { setEigenaarFilter(v); if (v !== "afdeling") setAfdelingFilter("alle"); };
  const [bedrijfsleiderFilter, setBedrijfsleiderFilter] = useState(initialPrefs.bedrijfsleiderFilter ?? "");
  const [omFilter, setOmFilter] = useState(initialPrefs.omFilter ?? "alle");
  const [besluitFilter, setBesluitFilter] = useState(initialPrefs.besluitFilter === true);
  const [locaties, setLocaties] = useState([]);
  const [oms, setOms] = useState([]);
  const [locationOms, setLocationOms] = useState({});
  const [generalBuckets, setGeneralBuckets] = useState([]);
  const [internKandidaten, setInternKandidaten] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [afsluit, setAfsluit] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const { isCollapsed, toggle: toggleGroup } = usePersistentCollapse("afspraken-groepen", user.username);
  const { matches: locMatches, active: myLocActive, selected: myLocs, setSelected: setMyLocs } = useMyLocations(user.username);
  const isOm = user.role === "om";
  const isOversight = user.role === "oversight";
  const wide = useMediaQuery("(min-width: 1100px)");
  const savedNotesRef = useRef({}); // laatst-OPGESLAGEN peilmoment-notities per `${afspraakId}|${date}`
  const afsprakenRef = useRef([]);
  afsprakenRef.current = afspraken;

  useEffect(() => {
    try {
      localStorage.setItem(prefsKey, JSON.stringify({ view, sortBy, opvolger: opvolgerFilter, opvolgerGekozen, eigenaarFilter, afdelingFilter, bedrijfsleiderFilter, omFilter, besluitFilter }));
    } catch { /* ignore */ }
  }, [prefsKey, view, sortBy, opvolgerFilter, opvolgerGekozen, eigenaarFilter, afdelingFilter, bedrijfsleiderFilter, omFilter, besluitFilter]);

  const loadMeta = () => api.get("/meta").then((r) => { setLocaties(r.data.locations); setOms(r.data.oms || []); setLocationOms(r.data.locationOms || {}); setGeneralBuckets(r.data.generalBuckets || []); setInternKandidaten(r.data.internKandidaten || []); setBlContact(r.data.blContact || {}); setAfdelingen(r.data.afdelingen || []); setMetaError(""); }).catch((e) => setMetaError(errMessage(e)));

  const loadSeq = useRef(0);
  const cpQueue = useRef({});
  const cpServer = useRef({});    // laatst bekende serverstand van de peilmomenten per afspraak
  const cpTouched = useRef({});   // loadSeq op het moment van de laatste lokale vink
  const extraRef = useRef([]);
  extraRef.current = extraAf;
  const laadVerwijderd = () => {
    const seq = loadSeq.current;
    return api.get("/afspraken?verwijderd=1").then((r) => {
      if (seq !== loadSeq.current) return;
      setVerwijderdeLijst(r.data); setVerwijderdError(""); setVerwijderdGeladen(true);
    }).catch((e) => { if (seq === loadSeq.current) setVerwijderdError(errMessage(e)); });
  };
  const load = () => {
    const seq = ++loadSeq.current;
    if (isOversight) laadVerwijderd();
    api.get(`/afspraken${volledigeHistorie ? "?historieWeken=0" : ""}`).then((r) => {
      if (seq !== loadSeq.current) return;   // een nieuwere load of een lokale wijziging is sneller geweest
      // Een notitie die nog getypt wordt (lokaal ≠ opgeslagen) overleeft het herladen.
      const dirty = {};
      for (const a of afsprakenRef.current) for (const c of (a.checkpoints || [])) {
        const k = noteKey(a.id, cidOf(c));
        if ((c.note || "") !== (savedNotesRef.current[k] || "")) dirty[k] = c.note || "";
      }
      const notes = {};
      const metNoten = (a0) => {
        const local = afsprakenRef.current.find((x) => x.id === a0.id);
        // v19-N6 F1: een vinkje dat na het starten van deze load is gezet (of nog onderweg is), wint van de lijst.
        const lokaalNieuwer = local && local.checkpoints && (cpQueue.current[a0.id]?.busy || (cpTouched.current[a0.id] || 0) >= seq);
        if (!lokaalNieuwer) cpServer.current[a0.id] = a0.checkpoints;
        const a = lokaalNieuwer ? { ...a0, checkpoints: local.checkpoints } : a0;
        return !a.checkpoints ? a : {
        ...a, checkpoints: a.checkpoints.map((c) => {
          const k = noteKey(a.id, cidOf(c));
          notes[k] = c.note || "";
          if (lokaalNieuwer) { notes[k] = savedNotesRef.current[k] || ""; return c; }
          return k in dirty ? { ...c, note: dirty[k] } : c;
        }),
      };
      };
      const data = r.data.map(metNoten);
      savedNotesRef.current = notes;
      setAfspraken(data);
      // Afspraken die via ?select= buiten het venster zijn geopend: vervang door de lijst, ververs de rest of laat ze los.
      const rest = extraRef.current.filter((x) => !data.some((d) => d.id === x.id));
      setExtraAf((l) => l.filter((x) => rest.some((y) => y.id === x.id)));
      rest.forEach((x) => {
        api.get(`/afspraken/${x.id}`).then((rr) => { if (seq === loadSeq.current) setExtraAf((l) => l.map((y) => (y.id === x.id ? metNoten(rr.data) : y))); })
          .catch((e) => { if (e?.response?.status === 404 && seq === loadSeq.current) setExtraAf((l) => l.filter((y) => y.id !== x.id)); });
      });
      setWeggelaten(parseInt(r.headers["x-afspraken-weggelaten"] || "0", 10) || 0);
      setHistorieWeken(parseInt(r.headers["x-afspraken-historie-weken"] || "0", 10) || 0);
      setLoaded(true);
      setLoadError("");
    }).catch((e) => { if (seq === loadSeq.current) setLoadError(errMessage(e)); });
  };
  const mutateAf = useCallback((id, fn, herlaad) => {
    const upd = (list) => list.map((a) => (a.id === id ? fn(a) : a));
    afsprakenRef.current = upd(afsprakenRef.current);   // v19-N6 F1: een snelle tweede tik ziet de eerste al
    setAfspraken(upd); setExtraAf(upd);
    if (herlaad) { loadSeq.current += 1; loadRef.current(); }   // een lijst die al onderweg was, mag dit niet overschrijven
  }, []);
  const schrijfFout = useCallback((e, standaard) => {
    if (e?.response?.status === 404) { toast.error("Deze afspraak bestaat niet meer of is verwijderd."); loadRef.current(); }
    else toast.error(e?.response?.data?.detail || standaard);
  }, []);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => { loadMeta(); }, []);
  useEffect(load, [volledigeHistorie]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deeplink vanuit het locatiedossier: /afspraken?select=<id> selecteert en scrollt naar die afspraak.
  const [searchParams] = useSearchParams();
  const selectParam = searchParams.get("select");
  const selectDone = useRef(false);
  useEffect(() => {
    if (!loaded || !selectParam || selectDone.current) return;
    if (isOversight && !verwijderdGeladen && !verwijderdError) return;
    const kies = (a) => {
      if (a.status !== "open") setTab("afgerond");
      setSelectedId(a.id);
      setTimeout(() => document.querySelector(`[data-testid="afspraak-${a.id}"]`)?.scrollIntoView({ block: "center" }), 200);
    };
    const a = afspraken.find((x) => x.id === selectParam);
    const w = verwijderdeLijst.find((x) => x.id === selectParam);
    selectDone.current = true;
    if (a) { kies(a); return; }
    if (w) { setTab("verwijderd"); setSelectedId(w.id); return; }
    api.get(`/afspraken/${selectParam}`).then((r) => { cpServer.current[r.data.id] = r.data.checkpoints; setExtraAf((l) => [...l, r.data]); kies(r.data); })
      .catch(() => toast.error("Deze afspraak bestaat niet meer of is verwijderd."));
  }, [loaded, selectParam, afspraken, verwijderdeLijst, verwijderdGeladen, verwijderdError, isOversight]);

  // v19-N6 F1: per afspraak één PATCH tegelijk; daarna gaat de nieuwste lokale stand mee als die intussen veranderde.
  // Notities worden vooraf als opgeslagen gemarkeerd (blur + sluiten schrijven nooit dubbel); bij een fout gaat dat terug.
  const saveCheckpoints = useCallback((id) => {
    const q = cpQueue.current[id] || (cpQueue.current[id] = {});
    if (q.busy) { q.again = true; return q.busy; }
    const start = afsprakenRef.current.find((x) => x.id === id);
    if (!start || !start.checkpoints) return Promise.resolve();   // v20-R1 R13: nooit een busy die blijft hangen
    q.busy = (async () => {
      await null;
      do {
        q.again = false;
        const a = afsprakenRef.current.find((x) => x.id === id);
        if (!a || !a.checkpoints) break;
        const prev = {};
        for (const c of a.checkpoints) { const k = noteKey(id, cidOf(c)); prev[k] = savedNotesRef.current[k]; savedNotesRef.current[k] = c.note || ""; }
        try {
          const r = await api.patch(`/afspraken/${id}/checkpoints`, { checkpoints: a.checkpoints.map((c) => ({ id: c.id, date: c.date, wat: c.wat || "", done: !!c.done, note: c.note || "" })) });
          cpServer.current[id] = r.data.checkpoints;
          cpTouched.current[id] = loadSeq.current;   // v20-R1 R12: een lijst die vóór dit antwoord startte, wint niet
          if (!q.again) window.dispatchEvent(new CustomEvent("nav-counts:refresh"));
          if (!q.again) {
            // Serverstand van de peilmomenten overnemen; een notitie die intussen is getypt blijft staan.
            // v20-R1 R8: ook wat enrich_afspraak uit de peilmomenten afleidt.
            mutateAf(id, (cur) => ({ ...cur, overdue: r.data.overdue, effectiveDeadline: r.data.effectiveDeadline,
              wachtOpOm: r.data.wachtOpOm, wachtOpOmSinds: r.data.wachtOpOmSinds, wachtOpOmLabel: r.data.wachtOpOmLabel, meldingen: r.data.meldingen,   // v22
              klaarOmAfTeRonden: r.data.klaarOmAfTeRonden, checkpoints: (r.data.checkpoints || []).map((c) => {
              const l = (cur.checkpoints || []).find((x) => cidOf(x) === cidOf(c));
              return l && (l.note || "") !== (savedNotesRef.current[noteKey(id, cidOf(c))] || "") ? { ...c, note: l.note } : c;
            }) }));
          }
        } catch (e) {
          Object.assign(savedNotesRef.current, prev);
          q.again = false;
          const server = cpServer.current[id];
          if (server) mutateAf(id, (cur) => ({ ...cur, checkpoints: server.map((c) => {
            const l = (cur.checkpoints || []).find((x) => cidOf(x) === cidOf(c));
            return l ? { ...c, note: l.note } : c;
          }) }));
          schrijfFout(e, "Peilmoment bijwerken mislukt");
        }
      } while (q.again);
      q.busy = null;
    })();
    return q.busy;
  }, [schrijfFout, mutateAf]);

  const flushNotes = useCallback((id) => {
    const a = afsprakenRef.current.find((x) => x.id === id);
    if (!a || !a.checkpoints) return;
    const dirty = a.checkpoints.some((c) => (c.note || "") !== (savedNotesRef.current[noteKey(id, cidOf(c))] || ""));
    if (dirty) saveCheckpoints(id);
  }, [saveCheckpoints]);

  const toggleCheckpoint = (a, cid) => {
    cpTouched.current[a.id] = loadSeq.current;
    mutateAf(a.id, (x) => ({ ...x, checkpoints: (x.checkpoints || []).map((c) => (cidOf(c) === cid ? { ...c, done: !c.done } : c)) }));
    saveCheckpoints(a.id);
  };
  const setNoteLocal = (id, cid, note) => mutateAf(id, (a) => ({ ...a, checkpoints: (a.checkpoints || []).map((c) => (cidOf(c) === cid ? { ...c, note } : c)) }));

  const changeOpvolger = async (a, opvolger) => {
    try {
      const r = await api.patch(`/afspraken/${a.id}/opvolger`, { opvolger });
      const { opvolgerEffectief, opvolgerActief } = r.data;
      mutateAf(a.id, (x) => ({ ...x, opvolger: r.data.opvolger, opvolgerEffectief, opvolgerActief }), true);
      toast.success(opvolgerEffectief ? `Opvolger: ${cap(opvolgerEffectief)}` : "Geen opvolger meer");
    } catch (e) {
      schrijfFout(e, "Opvolger wijzigen mislukt");
    }
  };
  const heropenen = async (a) => {
    try {
      await api.patch(`/afspraken/${a.id}/status`, { status: "open" });
      toast.success("Afspraak heropend — staat weer bij Lopend");
      load();
    } catch (e) {
      schrijfFout(e, "Heropenen mislukt");
    }
  };
  const doDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/afspraken/${deleting.id}`);
      toast.success("Afspraak verwijderd");
      if (deleting.id === selectedId) setSelectedId(null);
      setExtraAf((l) => l.filter((x) => x.id !== deleting.id));
      setDeleting(null);
      load();
    } catch (e) {
      if (e?.response?.status === 404) setDeleting(null);
      schrijfFout(e, "Verwijderen mislukt");
    }
  };
  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const onDialogChange = (o) => { setDialogOpen(o); if (!o) setEditing(null); };

  const bedrijfsleiders = useMemo(
    () => [...new Set(afspraken.map((a) => (a.bedrijfsleider || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, "nl")),
    [afspraken]
  );

  const alleAf = useMemo(() => [...afspraken, ...extraAf.filter((x) => !afspraken.some((a) => a.id === x.id))], [afspraken, extraAf]);
  afsprakenRef.current = alleAf;
  const lopend = useMemo(() => alleAf.filter((a) => a.status === "open"), [alleAf]);
  const afgerond = useMemo(() => alleAf.filter((a) => a.status !== "open"), [alleAf]);
  const tabItems = tab === "lopend" ? lopend : tab === "afgerond" ? afgerond : verwijderdeLijst;

  const common = useMemo(() => {
    const q = norm(zoek);
    const locForOm = (loc) => {
      if (omFilter === "alle") return true;
      const assigned = locationOms[loc];
      return !assigned || assigned.length === 0 || assigned.includes(omFilter);
    };
    return tabItems.filter((a) => {
      if (isOm && a.locatie && !generalBuckets.includes(a.locatie) && !locMatches(a.locatie)) return false;
      if (a.locatie && !locForOm(a.locatie)) return false;
      if (!pastEigenaarAfdeling(a, eigenaarFilter, afdelingFilter)) return false;
      if (bedrijfsleiderFilter && (a.bedrijfsleider || "").trim() !== bedrijfsleiderFilter) return false;
      // R26: "Geen" = geen ACTIEVE opvolger (ook een opvolger die uit dienst is)
      if (!pastOpvolger(a, opvolgerFilter)) return false;
      if (tab === "afgerond" && afgerondFilter !== "alle" && a.status !== afgerondFilter) return false;
      if (besluitFilter && !heeftBesluit(a)) return false;
      if (q && ![a.wat, a.locatie, a.bedrijfsleider, ...(a.betrokkenen || [])].some((v) => norm(v).includes(q)) && !reactieTreft(a, q)) return false;
      return true;
    });
  }, [tabItems, tab, zoek, omFilter, locationOms, isOm, generalBuckets, locMatches, eigenaarFilter, afdelingFilter, bedrijfsleiderFilter, opvolgerFilter, afgerondFilter, besluitFilter]);

  const snippets = useMemo(() => {
    const q = norm(zoek);
    const out = {};
    if (!q) return out;
    for (const a of common) {
      if ([a.wat, a.locatie, a.bedrijfsleider, ...(a.betrokkenen || [])].some((v) => norm(v).includes(q))) continue;
      const r = reactieTreft(a, q);
      if (r) out[a.id] = reactieSnippet(r.tekst, q);
    }
    return out;
  }, [common, zoek]);

  const onReacties = useCallback((id, fn) => mutateAf(id, (a) => ({ ...a, reacties: fn(a.reacties || []) }), true), [mutateAf]);

  const gaNaar = async (id) => {
    let a = alleAf.find((x) => x.id === id);
    if (!a) {
      try { a = (await api.get(`/afspraken/${id}`)).data; cpServer.current[id] = a.checkpoints; setExtraAf((l) => (l.some((x) => x.id === id) ? l : [...l, a])); }
      catch (e) { toast.error(e?.response?.status === 404 ? "Deze afspraak bestaat niet meer of is verwijderd." : (e?.response?.data?.detail || "Openen mislukt. Probeer het opnieuw.")); return; }
    }
    setTab(a.status === "open" ? "lopend" : "afgerond");
    setSelectedId(id);
  };

  const terugzettenBezig = useRef(false);
  const terugzetten = async (a) => {
    if (terugzettenBezig.current) return;
    terugzettenBezig.current = true;
    try {
      const r = await api.post(`/afspraken/${a.id}/terugzetten`);
      toast.success("Afspraak teruggezet");
      setExtraAf((l) => (l.some((x) => x.id === a.id) ? l : [...l, r.data]));   // ook zichtbaar als hij ouder is dan het venster
      setVerwijderdeLijst((l) => l.filter((x) => x.id !== a.id));
      setTab(r.data.status === "open" ? "lopend" : "afgerond");
      setSelectedId(a.id);
      load();
    } catch (e) {
      if (e?.response?.status === 404) { toast.error("Deze afspraak is al teruggezet."); load(); }
      else toast.error(e?.response?.data?.detail || "Terugzetten mislukt");
    } finally { terugzettenBezig.current = false; }
  };

  const counts = useMemo(() => {
    const c = { telaat: 0, dezeweek: 0, later: 0, geen: 0 };
    for (const a of common) c[groepVan(a)] += 1;
    return c;
  }, [common]);
  const zichtbaar = useMemo(
    () => (tab === "lopend" && teller ? common.filter((a) => groepVan(a) === teller) : common),
    [common, tab, teller]
  );

  const delen = [];
  if (opvolgerFilter !== "alle") delen.push(opvolgerFilter === GEEN_OPVOLGER ? "zonder actieve opvolger" : `opvolger ${cap(opvolgerFilter)}`);
  if (tab === "lopend" && teller) delen.push(GROEP[teller].label.toLowerCase());
  if (tab === "afgerond" && afgerondFilter !== "alle") delen.push(afgerondFilter === "nagekomen" ? "nagekomen" : "niet nagekomen");
  if (zoek.trim()) delen.push(`zoekterm "${zoek.trim()}"`);
  if (besluitFilter) delen.push("met besluit");
  if (eigenaarFilter !== "alle") delen.push(`eigenaar ${(EIGENAAR_LABEL[eigenaarFilter] || eigenaarFilter).toLowerCase()}`);
  if (afdelingFilter !== "alle") delen.push(`afdeling ${afdelingen.find((x) => x.id === afdelingFilter)?.naam || afdelingFilter}`);
  if (bedrijfsleiderFilter) delen.push(`bedrijfsleider ${bedrijfsleiderFilter}`);
  if (isOm && myLocActive) delen.push(`mijn locaties (${myLocs.length})`);
  if (omFilter !== "alle") delen.push(`locaties van ${cap(omFilter)}`);
  const filtersActief = delen.length > 0;
  const filtersInPaneel = [besluitFilter, eigenaarFilter !== "alle", afdelingFilter !== "alle", !!bedrijfsleiderFilter, isOm && myLocActive, omFilter !== "alle"].filter(Boolean).length;

  const allesTonen = () => {
    setOpvolgerFilter("alle"); setTeller(null); setZoek(""); setEigenaarFilter("alle"); setAfdelingFilter("alle");
    setBedrijfsleiderFilter(""); setOmFilter("alle"); setAfgerondFilter("alle"); setBesluitFilter(false);
    if (myLocActive) setMyLocs([]);
  };

  const groups = useMemo(() => {
    const map = {};
    for (const a of zichtbaar) {
      const k = groepSleutel(a);
      (map[k] = map[k] || []).push(a);
    }
    const cmp = compareRows(sortBy);
    return Object.entries(map).map(([key, items]) => ({
      key,
      intern: key === INTERN_KEY,
      label: groepLabel(key, items),
      items: [...items].sort(cmp),
      telaat: items.filter((i) => groepVan(i) === "telaat").length,
      dezeweek: items.filter((i) => groepVan(i) === "dezeweek").length,
    })).sort(compareGroepen(sortBy, generalBuckets));
  }, [zichtbaar, sortBy, generalBuckets]);

  // Opvolger-segmenten: de OM's, plus een oversight-eigenaar van een interne afspraak die jij mag zien.
  const opvolgerOpties = useMemo(() => {
    // v18-D7 / R1 R5: alleen actieve mensen als chip; afspraken van wie vertrokken is blijven onder "Geen".
    return opvolgerChips({ oms, afspraken, actief: internKandidaten, ik: user.username });
  }, [afspraken, oms, internKandidaten, user.username]);

  const afgerondLijst = useMemo(() => sorteerAfgerondLijst(zichtbaar, { sortBy, buckets: generalBuckets, verwijderd: tab === "verwijderd" }), [zichtbaar, tab, sortBy, generalBuckets]);
  const selected = selectedId ? tabItems.find((a) => a.id === selectedId) : null;

  const groepDefaultOpen = (g) => g.telaat > 0 || g.dezeweek > 0 || filtersActief || g.items.some((a) => a.id === selectedId);
  const isGroupOpen = (g) => !isCollapsed(g.key, !groepDefaultOpen(g));
  const onGroupToggle = (g) => toggleGroup(g.key, !groepDefaultOpen(g));

  const switchTab = (t) => { setTab(t); setSelectedId(null); setTeller(null); };
  const selectAfspraak = (a) => setSelectedId(a.id);
  const inline = wide && (tab !== "lopend" || view === "lijst");

  // Esc sluit ook het vaste paneel; het ontkoppelen van het detail slaat een getypte notitie op.
  useEffect(() => {
    if (!inline || !selectedId) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && !e.target?.closest?.("textarea, input") && !document.querySelector("[role=dialog], [role=alertdialog], [role=menu]")) setSelectedId(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [inline, selectedId]);

  const detail = selected && (
    <AfspraakDetail a={selected} oms={oms} me={user.username} onReacties={onReacties} onTerugzetten={terugzetten} onGaNaar={gaNaar} onClose={() => setSelectedId(null)} onOpvolger={changeOpvolger}
      onToggleCp={toggleCheckpoint} onNoteChange={setNoteLocal} onNoteFlush={flushNotes}
      onAfronden={(a, status) => setAfsluit({ afspraak: a, status })} onHeropenen={heropenen}
      onEdit={(a) => { setEditing(a); setDialogOpen(true); }} onDelete={setDeleting} />
  );

  const renderLijst = () => {
    if (tab === "verwijderd" && verwijderdError) return <LoadError error={verwijderdError} onRetry={laadVerwijderd} testid="afspraken-verwijderd-error" />;
    if (zichtbaar.length === 0) {
      return (
        <div className="bg-white rounded-[10px] border border-zinc-200 p-12 text-center" data-testid="afspraken-empty">
          <Inbox className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-sm text-zinc-500 font-medium">{filtersActief ? "Geen afspraken in deze filter." : tab === "lopend" ? "Geen lopende afspraken." : tab === "verwijderd" ? "Geen verwijderde afspraken." : "Nog geen afgeronde afspraken."}</p>
          {tab === "lopend" && !filtersActief && <p className="text-xs text-zinc-400 mt-1">Maak er één aan vanuit een locatie-call of via "Nieuwe afspraak".</p>}
        </div>
      );
    }
    if (tab !== "lopend") {
      return (
        <div data-testid={`afspraken-${tab}-lijst`} className="bg-white border border-zinc-200 rounded-[10px] divide-y divide-zinc-200">
          {afgerondLijst.map((a) => <AfspraakRow key={a.id} a={a} selected={a.id === selectedId} onSelect={selectAfspraak} />)}
        </div>
      );
    }
    if (view === "bord") return <AfspraakBoard items={zichtbaar} sortBy={sortBy} onSelect={selectAfspraak} />;
    return <LocatieGroepen groups={groups} isOpen={isGroupOpen} onToggle={onGroupToggle} selectedId={selectedId} onSelect={selectAfspraak} />;
  };

  return (
    <SnippetContext.Provider value={snippets}>
    <div data-testid="afspraken-page" className="pb-24 sm:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3 md:mb-4">
        <div>
          <h1 className="font-heading text-2xl font-black tracking-tight text-zinc-900">Afspraken</h1>
          <p className="text-sm text-zinc-500">De opvolg-motor — wie pakt wat op, en wat staat er te laat.</p>
        </div>
        <button data-testid="new-afspraak-btn" onClick={openCreate} className="hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-md bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2">
          <Plus className="h-4 w-4" /> Nieuwe afspraak
        </button>
      </div>

      <div className="flex items-end gap-6 border-b border-zinc-300 mb-4" role="tablist">
        {[["lopend", "Lopend", lopend.length], ["afgerond", "Afgerond", afgerond.length], ...(isOversight ? [["verwijderd", "Verwijderd", verwijderdeLijst.length]] : [])].map(([k, label, n]) => (
          <button key={k} role="tab" aria-selected={tab === k} data-testid={`tab-${k}`} onClick={() => switchTab(k)}
            className={`-mb-px pb-2 flex items-center gap-2 text-sm font-semibold border-b-2 transition-colors ${tab === k ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}>
            {label}
            <span data-testid={`tab-count-${k}`} className={`font-mono text-xs px-1.5 py-0.5 rounded ${tab === k ? "bg-zinc-950 text-white" : "bg-zinc-200 text-zinc-500"}`}>{n}</span>
          </button>
        ))}
      </div>

      {tab === "lopend" && loaded && <CounterTiles counts={counts} active={teller} onToggle={setTeller} />}

      <AfsprakenToolbar zoek={zoek} setZoek={setZoek} opvolger={opvolgerFilter} setOpvolger={kiesOpvolger}
        view={view} setView={(v) => { setView(v); setSelectedId(null); }} showView={tab === "lopend"} oms={oms} opvolgerOms={opvolgerOpties}
        f={{ eigenaar: eigenaarFilter, afdeling: afdelingFilter, bedrijfsleider: bedrijfsleiderFilter, om: omFilter, sort: sortBy, besluit: besluitFilter }}
        set={{ eigenaar: kiesEigenaar, afdeling: kiesAfdeling, bedrijfsleider: setBedrijfsleiderFilter, om: setOmFilter, sort: setSortBy, besluit: setBesluitFilter }}
        isOm={isOm} user={user} locaties={locaties} metaError={metaError} bedrijfsleiders={bedrijfsleiders} afdelingen={afdelingen} aantalActief={filtersInPaneel} />

      {tab === "afgerond" && (
        <div className="flex gap-1 mb-4 rounded-md border border-zinc-300 bg-white p-0.5 w-fit" data-testid="afgerond-filter">
          {AFGEROND_FILTERS.map((f) => (
            <button key={f.key} data-testid={`afgerond-filter-${f.key}`} onClick={() => setAfgerondFilter(f.key)} className={segBtn(afgerondFilter === f.key)}>{f.label}</button>
          ))}
        </div>
      )}

      {filtersActief && loaded && (
        <div data-testid="afspraken-filterbalk" className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-4 py-2.5 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-blue-900" data-testid="afspraken-filterbalk-tekst">
            Je ziet <span className="font-mono font-bold">{zichtbaar.length}</span> van {plural(tabItems.length, "afspraak", "afspraken")}: {delen.join(", ")}
          </p>
          <button data-testid="afspraken-alles-tonen" onClick={allesTonen} className="shrink-0 px-3 py-1.5 rounded-md bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700">
            Alles tonen
          </button>
        </div>
      )}

      {(loadError || metaError) && (
        <div className="mb-4 space-y-2">
          <LoadError error={loadError} onRetry={load} testid="afspraken-load-error" />
          <LoadError error={metaError} onRetry={loadMeta} testid="afspraken-meta-error" />
        </div>
      )}

      {tab === "afgerond" && weggelaten > 0 && (
        <div data-testid="afspraken-historie-melding" className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-2.5 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-amber-900">
            <span className="font-semibold">{plural(weggelaten, "afgeronde afspraak valt", "afgeronde afspraken vallen")}</span> buiten de laatste {historieWeken} weken en {weggelaten === 1 ? "staat" : "staan"} niet in deze lijst. Lopende afspraken worden altijd volledig getoond.
          </p>
          <button data-testid="afspraken-volledige-historie" onClick={() => setVolledigeHistorie(true)}
            className="shrink-0 px-3 py-1.5 rounded-md bg-amber-900 text-white text-xs font-semibold hover:bg-amber-800">
            Volledige historie laden
          </button>
        </div>
      )}
      {tab === "afgerond" && volledigeHistorie && (
        <p data-testid="afspraken-historie-volledig" className="mb-4 text-xs text-zinc-500">Volledige historie geladen — niets afgekapt.</p>
      )}

      {(loadError || metaError) ? null : !loaded ? (
        <div data-testid="afspraken-loading" className="bg-white rounded-[10px] border border-zinc-200 p-12 text-center text-sm text-zinc-400">Laden…</div>
      ) : inline ? (
        <div className="grid grid-cols-[minmax(0,1fr)_400px] gap-5 items-start">
          <div className="min-w-0">{renderLijst()}</div>
          <aside data-testid="afspraak-detail-panel" className="sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto bg-white border border-zinc-200 rounded-[10px]">
            {detail || <p data-testid="afspraak-detail-leeg" className="p-8 text-center text-sm text-zinc-400">Klik een afspraak aan om de details, peilmomenten en meldingen van de locatie te zien.</p>}
          </aside>
        </div>
      ) : renderLijst()}

      {!inline && (
        <Sheet open={!!selected} onOpenChange={(o) => { if (!o) setSelectedId(null); }}>
          <SheetContent side="right" className="w-full sm:max-w-lg p-0 overflow-y-auto" data-testid="afspraak-detail-sheet"
            onEscapeKeyDown={(e) => { if (e.target?.closest?.("textarea, input")) e.preventDefault(); }}>
            <SheetTitle className="sr-only">Afspraak details</SheetTitle>
            <SheetDescription className="sr-only">Details, peilmomenten en acties van de afspraak</SheetDescription>
            {selected && (
              <AfspraakDetail a={selected} oms={oms} me={user.username} onReacties={onReacties} onTerugzetten={terugzetten} onGaNaar={gaNaar} onOpvolger={changeOpvolger}
                onToggleCp={toggleCheckpoint} onNoteChange={setNoteLocal} onNoteFlush={flushNotes}
                onAfronden={(a, status) => setAfsluit({ afspraak: a, status })} onHeropenen={heropenen}
                onEdit={(a) => { setEditing(a); setDialogOpen(true); }} onDelete={setDeleting} />
            )}
          </SheetContent>
        </Sheet>
      )}

      <div data-testid="new-afspraak-bar-mobile" style={{ bottom: "calc(57px + env(safe-area-inset-bottom))" }} className="sm:hidden fixed inset-x-0 z-40 pl-4 pr-[96px] pb-3 pt-6 bg-gradient-to-t from-[#F4F4F5] via-[#F4F4F5]/95 to-transparent">
        <button data-testid="new-afspraak-btn-mobile" onClick={openCreate} className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-md bg-zinc-950 text-white text-sm font-semibold shadow-lg active:scale-[0.99] transition-transform">
          <Plus className="h-5 w-5" /> Nieuwe afspraak
        </button>
      </div>

      <AfspraakDialog open={dialogOpen} onOpenChange={onDialogChange} week={currentWeek()} locaties={locaties} generalBuckets={generalBuckets} oms={oms} internKandidaten={internKandidaten} blContact={blContact} afdelingen={afdelingen} defaultLocatie="" afspraak={editing} onCreated={load} />
      <AfspraakAfsluitDialog open={!!afsluit} onOpenChange={(o) => { if (!o) setAfsluit(null); }} afspraak={afsluit?.afspraak} status={afsluit?.status} onSaved={() => { setAfsluit(null); load(); }} />
      <AlertDialog open={!!deleting} onOpenChange={(o) => { if (!o) setDeleting(null); }}>
        <AlertDialogContent data-testid="delete-afspraak-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Afspraak verwijderen?</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line break-words">
              {deleting?.wat ? `"${deleting.wat}"` : ""}{"\n"}De afspraak verdwijnt voor iedereen. Oversight kan hem terugzetten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-afspraak-cancel">Annuleren</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-afspraak-confirm" onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">Verwijderen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </SnippetContext.Provider>
  );
}
